// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo, useState } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import {
    CaseTemplateOutput,
    PAPValueArray,
    PAPValues,
    SeverityValueArray,
    SeverityValues,
    TLPValueArray,
    TLPValues,
    TaskInput,
    TaskOutput,
    CustomFieldValueInput,
    PageTemplateInput,
} from 'thehive-sdk';
import { SecurityInput } from '@Atoms/Inputs/SecurityInput/SecurityInput';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { TaskManage } from '@Organisms/Task/TaskManage/TaskManage';
import { AddItemButton } from '@Atoms/Buttons';
import { CreateSortableListItem } from '@Molecules/CreateSortableListItem/CreateSortableListItem';
import { CustomFieldValueManage } from '@Organisms/CustomField/CustomFieldValueManage/CustomFieldValueManage';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { onOrganisationImageUpload } from '@Helpers/download';
import { PageTemplateManage } from '@Organisms/PageTemplate/PageTemplateManage/PageTemplateManage';
import { PageTemplateFormated } from '@Organisms/PageTemplate/PageTemplateExisting/PageTemplateExisting';
import { computeEventList } from '@Organisms/KnowledgeBase/eventList';
import { computeEventList as computeEventListPageTemplate } from '@Organisms/PageTemplate/eventList';
import client from '@Client';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import useListQuery from '@Hooks/useListQuery';
import { useDrawer } from '@Hooks/useDrawer';
import { CreateListItem } from '@Molecules/CreateListItem/CreateListItem';

import './CaseTemplateForm.scss';

export interface CaseTemplateValuesForm {
    description: string;
    displayName: string;
    name: string;
    pap: PAPValues;
    severity: SeverityValues;
    tags: string[];
    titlePrefix: string;
    tlp: TLPValues;
    tasks: TaskInput[];
    customFields: CustomFieldValueInput[];
    pageTemplateIds: string[];
    pageTemplates?: PageTemplateFormated[];
}

interface CaseTemplateFormProps {
    onFinish(values: CaseTemplateValuesForm): Promise<any>;
    form: FormInstance;
    defaultValues?: CaseTemplateOutput;
    isValid: boolean;
    onChange: () => void;
    onCancel: () => void;
}

function sortByOrder(a: { order: number }, b: { order: number }) {
    return a.order - b.order;
}

function editOrder(item: any, index: number) {
    return { ...item, order: index };
}

const CaseTemplateForm = ({
    onFinish,
    form,
    defaultValues,
    isValid,
    onChange,
    onCancel,
}: CaseTemplateFormProps): ReactElement => {
    const i = useLocalMessage();
    const [loading, setLoading] = useState(false);

    const { visible: pageTemplatesVisible, setVisible: setPageTemplatesVisible } = useDrawer({});

    const { tlp = 2, pap = 2, severity = 1 } = defaultValues || {};

    const tradLabelKey = !!defaultValues ? 'edit' : 'create';

    const { data: pageTemplateList = [], refetch: listRefetch } = useListQuery({
        controller: client.PageTemplateController,
        queryParams: {
            selector: { head: [{ name: 'listPageTemplate' }], name: 'get-all-page-templates' },
        },
        fetchOnStart: true,
    });

    if (defaultValues?.tasks) {
        defaultValues?.tasks.sort(sortByOrder);
    }

    if (defaultValues?.customFields) {
        defaultValues?.customFields.sort(sortByOrder);
    }

    if (defaultValues?.extraData?.pageTemplateHeaders) {
        const defaultPageTemplates: PageTemplateFormated[] = [];
        defaultValues?.extraData?.pageTemplateHeaders.forEach((template: PageTemplateFormated) => {
            defaultPageTemplates.push(pageTemplateList.find((item) => item._id === template._id));
        });
        defaultValues.pageTemplates = defaultPageTemplates;
    }

    const eventList = useMemo(() => {
        if (window.location.pathname.match('/organisations/')) {
            return computeEventListPageTemplate({
                controller: client.PageTemplateController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            });
        } else {
            return computeEventList({
                controller: client.PageController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            });
        }
    }, [listRefetch, i]);

    const sortTemplatesArray = (templates: PageTemplateFormated[]) => {
        templates.sort(function (a, b) {
            // Compare categories
            const categorieComparison = a.category.localeCompare(b.category);
            if (categorieComparison !== 0) {
                return categorieComparison;
            }
            // If the categories are identical, compare the titles
            return a.title.localeCompare(b.title);
        });
        return templates;
    };

    const onFinishHandler = async (values: CaseTemplateValuesForm) => {
        setLoading(true);
        await listRefetch();

        if (values?.tasks) {
            values.tasks = values?.tasks.map(editOrder);
        }

        if (values?.customFields) {
            values.customFields = values.customFields.map(editOrder);
        }

        if (values?.pageTemplates) {
            const pageTemplateIds: string[] = [];
            values.pageTemplates.forEach((templateSelected) => {
                pageTemplateIds.push(
                    pageTemplateList.find((item) => {
                        if (templateSelected._id) {
                            // if page template already saved in db
                            return item._id === templateSelected._id;
                        } else {
                            // if page template just created
                            return (
                                templateSelected.title === item.title &&
                                templateSelected.category === item.category &&
                                templateSelected.content === item.content
                            );
                        }
                    })?._id,
                );
            });
            values.pageTemplateIds = pageTemplateIds;
            delete values.pageTemplates;
        }

        onFinish(values).finally(() => {
            setLoading(false);
        });
    };

    return (
        <THForm<CaseTemplateValuesForm>
            name="case-template-form"
            onFinish={onFinishHandler}
            form={form}
            initialValues={{ tlp, pap, severity, ...defaultValues }}
            onValuesChange={onChange}
            submitTouchedValues={!!defaultValues}
            className="case-template-form"
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={loading}
                        onCancel={onCancel}
                        disabled={!isValid}
                        footerText={i(`case-template.${tradLabelKey}.form.submit`)}
                    />
                }
            >
                {['titlePrefix', 'name', 'displayName'].map((field) => {
                    const required = !['titlePrefix', 'displayName'].includes(field);
                    return (
                        <THForm.Item
                            key={field}
                            label={i(`case-template.create.form.${field}.label`)}
                            name={field}
                            rules={[
                                {
                                    required,
                                    message: i(`case-template.create.form.${field}.required`),
                                },
                            ]}
                        >
                            <THInput placeholder={i(`case-template.create.form.${field}.placeholder`)} />
                        </THForm.Item>
                    );
                })}

                <div className="case-template-form__section">
                    <THForm.Item name="tlp" label={i('case-template.create.form.tlp.label')}>
                        <SecurityInput type="tlp" valueArray={TLPValueArray} />
                    </THForm.Item>

                    <THForm.Item name="pap" label={i('case-template.create.form.pap.label')}>
                        <SecurityInput type="pap" valueArray={PAPValueArray} />
                    </THForm.Item>

                    <THForm.Item name="severity" label={i('case-template.create.form.severity.label')}>
                        <SecurityInput type="severity" hasLabel={false} valueArray={SeverityValueArray} />
                    </THForm.Item>
                </div>

                <THForm.Item label={i('case-template.create.form.tags.label')} name="tags">
                    <THTagSelector.Autocomplete placeholder={i('common.tags')} />
                </THForm.Item>

                <THForm.Item
                    label={i('case-template.create.form.description.label')}
                    name="description"
                    rules={[{ required: true, message: i('case-template.create.form.description.required') }]}
                >
                    <THMarkdown sameSize onImageUpload={onOrganisationImageUpload} />
                </THForm.Item>

                <THForm.List name="tasks">
                    {(fields, { add, remove }): ReactElement => (
                        <>
                            <TitleSection
                                className="case-template-form__title-section"
                                extraAction={
                                    <TaskManage
                                        needDueDate={false}
                                        onFinish={(value) => {
                                            add(value);
                                            onChange();
                                            return Promise.resolve(value);
                                        }}
                                        triggerElem={<AddItemButton />}
                                    />
                                }
                            >
                                {i('case.create.form.tasks.label')}
                            </TitleSection>

                            {fields.map((field, index) => {
                                const task = form.getFieldValue('tasks')?.[field.name] as TaskOutput | undefined;

                                return (
                                    <CreateSortableListItem
                                        editDrawer={(onFinish, triggerElem) => (
                                            <TaskManage
                                                onFinish={(value): Promise<any> => {
                                                    onFinish(value);
                                                    onChange();

                                                    return Promise.resolve(value);
                                                }}
                                                triggerElem={triggerElem}
                                                defaultValues={task}
                                                needDueDate={false}
                                            />
                                        )}
                                        title={`${task?.group} - ${task?.title}`}
                                        fieldName="tasks"
                                        key={field.key}
                                        field={field}
                                        index={index}
                                        remove={remove}
                                        form={form}
                                        onMove={() => onChange()}
                                    />
                                );
                            })}
                        </>
                    )}
                </THForm.List>

                <THForm.List name="customFields">
                    {(fields, { add, remove }): ReactElement => (
                        <>
                            <TitleSection
                                className="case-template-form__title-section"
                                extraAction={
                                    <CustomFieldValueManage
                                        onOpen={() => {
                                            onChange();
                                        }}
                                        onFinish={(value) => {
                                            add(value);
                                            onChange();
                                        }}
                                        triggerElem={<AddItemButton />}
                                    />
                                }
                            >
                                {i('case.create.form.custom-fields.label')}
                            </TitleSection>

                            {fields.map((field, index) => {
                                const customField = form.getFieldValue('customFields')?.[field.name] as
                                    | CustomFieldValueInput
                                    | undefined;

                                return (
                                    <CreateSortableListItem
                                        editDrawer={(onFinish, triggerElem) => (
                                            <CustomFieldValueManage
                                                onFinish={(value) => {
                                                    onFinish(value);
                                                    onChange();
                                                }}
                                                triggerElem={triggerElem}
                                                defaultValues={customField}
                                            />
                                        )}
                                        title={`${customField?.type} - ${customField?.name}`}
                                        fieldName="customFields"
                                        key={field.key}
                                        field={field}
                                        index={index}
                                        remove={remove}
                                        form={form}
                                        onMove={() => onChange()}
                                    />
                                );
                            })}
                        </>
                    )}
                </THForm.List>

                <THForm.List name="pageTemplates">
                    {(fields, { add, remove }): ReactElement => (
                        <>
                            <TitleSection
                                className="case-template-form__title-section"
                                extraAction={<AddItemButton onClick={() => setPageTemplatesVisible(true)} />}
                            >
                                {i('page-template.title')}
                            </TitleSection>

                            {fields.map((field) => {
                                const sortedPageTemplates = sortTemplatesArray(form.getFieldValue('pageTemplates'));
                                const pageTemplate = Array.from(new Set(sortedPageTemplates))?.[field.name] as
                                    | PageTemplateInput
                                    | undefined;

                                return (
                                    <CreateListItem<PageTemplateInput | undefined>
                                        title={`${pageTemplate?.category} - ${pageTemplate?.title}`}
                                        key={field.key}
                                        field={field}
                                        remove={remove}
                                        removeLabel={i('general.remove')}
                                        form={form}
                                    />
                                );
                            })}
                            {pageTemplatesVisible && (
                                <PageTemplateManage
                                    drawerTitle={i('page-template.actions.create')}
                                    ctaMode={['create-template', 'use']}
                                    onOpen={onChange}
                                    onCreate={(page) => eventList.onCreate(page)}
                                    onFinish={(pageTemplate) => {
                                        add(pageTemplate);
                                        onChange();
                                    }}
                                    visible={pageTemplatesVisible}
                                    setVisible={setPageTemplatesVisible}
                                />
                            )}
                        </>
                    )}
                </THForm.List>
            </THForm.Layout>
        </THForm>
    );
};

export default CaseTemplateForm;
